import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_STARTER_PACKS,
  INSTALL_STARTER_PACK,
} from '../graphql/starter-pack-queries';
import { GET_WORDS } from '../graphql/queries';

/**
 * PackBrowser — post-onboarding pack picker. Listing all HSK packs with
 * installed-count hints (counted from words tagged `hskN-starter`) so teachers
 * can add HSK 2–6 after upgrading to Pro without re-running onboarding.
 */
function PackBrowser({ teacher, onBack, onInstalled }) {
  const { data, loading, error } = useQuery(GET_STARTER_PACKS);
  const { data: wordsData } = useQuery(GET_WORDS);
  const [installPack, { loading: installing }] = useMutation(INSTALL_STARTER_PACK);
  const [activePackId, setActivePackId] = useState(null);
  const [installError, setInstallError] = useState(null);
  const [justInstalled, setJustInstalled] = useState(null);

  const isPro = teacher?.plan === 'pro';
  const packs = data?.starterPacks || [];

  // Map pack hskLevel → count of words already tagged with the pack's topic.
  // Used to show "X words already added" hints, not a hard "installed" gate.
  const installedByLevel = useMemo(() => {
    const counts = {};
    const allWords = wordsData?.words || [];
    for (const w of allWords) {
      for (const t of w.topics || []) {
        const m = /^hsk(\d+)-starter$/.exec(t);
        if (m) {
          const lvl = parseInt(m[1], 10);
          counts[lvl] = (counts[lvl] || 0) + 1;
        }
      }
    }
    return counts;
  }, [wordsData]);

  const handleInstall = async (pack) => {
    if (pack.proOnly && !isPro) return;
    setActivePackId(pack.id);
    setInstallError(null);
    setJustInstalled(null);
    try {
      const result = await installPack({
        variables: { packId: pack.id },
        refetchQueries: [{ query: GET_WORDS }],
      });
      const insertedCount = result?.data?.installStarterPack?.length || 0;
      setJustInstalled({ id: pack.id, count: insertedCount });
      if (onInstalled) onInstalled();
    } catch (err) {
      setInstallError(err.message);
    } finally {
      setActivePackId(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <button className="btn btn--ghost btn--sm" onClick={onBack}>
          ← Back
        </button>
        <span style={{ fontWeight: 500 }}>Starter packs</span>
        <div style={{ width: 80 }} />
      </header>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px clamp(20px, 4vw, 80px)' }}>
        <div style={{ marginBottom: 32 }}>
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>
            Vocabulary packs · 词包
          </div>
          <h1 className="t-h1" style={{ marginBottom: 12 }}>
            Add a curated HSK pack.
          </h1>
          <p className="t-body" style={{ maxWidth: 640 }}>
            Each pack adds vocabulary tagged with its HSK level so you can filter and study by level.
            Re-installing skips words you already have — safe to click again.
          </p>
        </div>

        {loading && <p className="t-body">Loading packs…</p>}

        {error && (
          <div
            className="card"
            style={{
              padding: 24,
              borderColor: 'var(--vermillion)',
              background: 'var(--vermillion-50)',
            }}
          >
            <p className="t-body" style={{ color: 'var(--vermillion-ink)' }}>
              Couldn't load starter packs: {error.message}
            </p>
          </div>
        )}

        {!loading && !error && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
            }}
          >
            {packs.map((pack) => {
              const locked = pack.proOnly && !isPro;
              const isInstalling = activePackId === pack.id && installing;
              const installedCount = installedByLevel[pack.hskLevel] || 0;
              const isJust = justInstalled?.id === pack.id;
              return (
                <div
                  key={pack.id}
                  style={{
                    position: 'relative',
                    background: locked ? 'var(--paper-2)' : 'var(--card)',
                    border: '1px solid var(--rule)',
                    borderRadius: 12,
                    padding: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 260,
                    opacity: locked ? 0.7 : 1,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 14,
                    }}
                  >
                    <span className="badge badge--mono">HSK {pack.hskLevel}</span>
                    {pack.proOnly ? (
                      <span className="badge badge--gold">Pro</span>
                    ) : (
                      <span className="badge badge--jade">Free</span>
                    )}
                  </div>

                  <h2 className="t-h2" style={{ marginBottom: 4 }}>{pack.title}</h2>
                  <div className="pinyin" style={{ fontSize: 14, marginBottom: 12 }}>
                    {pack.subtitle}
                  </div>
                  <p
                    className="t-small"
                    style={{ flex: 1, marginBottom: 14, lineHeight: 1.5, color: 'var(--ink-2)' }}
                  >
                    {pack.description}
                  </p>

                  {installedCount > 0 && !isJust && (
                    <p
                      className="t-small"
                      style={{ color: 'var(--jade)', marginBottom: 10, fontSize: 12 }}
                    >
                      {installedCount} word{installedCount === 1 ? '' : 's'} already in your library.
                    </p>
                  )}
                  {isJust && (
                    <p
                      className="t-small"
                      style={{ color: 'var(--jade)', marginBottom: 10, fontSize: 12 }}
                    >
                      ✓ Added {justInstalled.count} new word{justInstalled.count === 1 ? '' : 's'}.
                    </p>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 12,
                      borderTop: '1px solid var(--rule-2)',
                    }}
                  >
                    <span className="t-small">
                      <span className="t-mono">{pack.wordCount}</span> words
                    </span>
                    {locked ? (
                      <span className="t-small" style={{ color: 'var(--gold)' }}>Pro only</span>
                    ) : (
                      <button
                        className="btn btn--accent btn--sm"
                        onClick={() => handleInstall(pack)}
                        disabled={isInstalling || installing}
                      >
                        {isInstalling ? 'Installing…' : (installedCount > 0 ? 'Add more →' : 'Install →')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {installError && (
          <p
            style={{
              textAlign: 'center',
              color: 'var(--error)',
              fontSize: 13,
              marginTop: 16,
            }}
          >
            {installError}
          </p>
        )}

        {!isPro && (
          <div
            style={{
              marginTop: 24,
              padding: 16,
              background: 'var(--paper-2)',
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r3)',
              textAlign: 'center',
            }}
          >
            <p className="t-small" style={{ margin: 0 }}>
              Free plan: HSK 1 only · 50-word library cap. Upgrade to Pro for HSK 2–6 and unlimited words.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PackBrowser;
